import React, { useState, useEffect, useCallback } from 'react'
import styled from 'styled-components'
import { Toast } from 'antd-mobile'
import { Spin, Button, QRCode } from 'antd'
import {
  LinkOutlined,
  InfoCircleOutlined,
  DownloadOutlined,
  CopyOutlined,
  QrcodeOutlined
} from '@ant-design/icons'
import api from '../utils/api'
import Hint from './common/Hint'
import { useAuth } from '../hooks/AuthContext'

const FormContainer = styled.form`
  max-width: 800px;
  margin: 0 auto;
  padding: 10px;
`

const FormGroup = styled.div`
  margin-bottom: 20px;
`

const Label = styled.label`
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
`

const Input = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #d5728a;
    box-shadow: 0 0 0 2px rgba(213, 114, 138, 0.2);
  }

  &:disabled {
    background-color: #f5f5f5;
    color: #999;
    cursor: not-allowed;
  }
`

const SwitchLabel = styled.label`
  position: relative;
  display: inline-block;
  width: 55px;
  height: 25px;
  margin-left: 10px;
`

const SwitchInput = styled.input`
  opacity: 0;
  width: 0;
  height: 0;

  &:checked + span {
    background-color: #d5728a;
  }

  &:focus + span {
    box-shadow: 0 0 1px #d5728a;
  }

  &:checked + span:before {
    -webkit-transform: translateX(27px);
    -ms-transform: translateX(27px);
    transform: translateX(27px);
  }
`

const Slider = styled.span`
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  -webkit-transition: 0.4s;
  transition: 0.4s;
  border-radius: 34px;

  &:before {
    position: absolute;
    content: '';
    height: 20px;
    width: 20px;
    left: 4px;
    bottom: 3px;
    background-color: white;
    -webkit-transition: 0.4s;
    transition: 0.4s;
    border-radius: 50%;
  }
`

const SwitchContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 20px;
`

const SwitchText = styled.span`
  font-size: 18px;
  font-weight: bold;
`

const ErrorMessage = styled.span`
  color: red;
  font-size: 12px;
  margin-top: 5px;
  display: block;
`

const HintText = styled.p`
  font-size: 12px;
  color: #666;
  margin-top: 5px;
  line-height: 1.4;
`

const ButtonGroup = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
`

const StyledButton = styled(Button)`
  height: 50px;
  font-size: 16px;
  min-width: 120px;
`

const InfoCard = styled.div`
  background: #f0f8ff;
  border: 1px solid #b3d9ff;
  border-radius: 6px;
  padding: 16px;
  margin-bottom: 20px;
`

const InfoTitle = styled.h4`
  color: #1890ff;
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`

const InfoText = styled.p`
  margin: 0;
  color: #666;
  font-size: 14px;
  line-height: 1.5;
`

const LinkButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #d5728a;
  text-decoration: none;
  font-weight: 500;

  &:hover {
    color: #b8506a;
    text-decoration: underline;
  }
`

const WebhookContainer = styled.div`
  background: #f9f9f9;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  padding: 12px;
  font-family: monospace;
  font-size: 13px;
  word-break: break-all;
  color: #333;
`

const StatusText = styled.span`
  color: ${(props) => (props.enabled ? '#52c41a' : '#ff4d4f')};
  font-size: 12px;
  margin-left: 8px;
  font-weight: bold;
`

const QrCard = styled.div`
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 20px 16px;
  margin-bottom: 24px;
  text-align: center;
`

const QrTitle = styled.h4`
  color: #0f2562;
  margin: 0 0 4px 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 16px;
`

const QrDesc = styled.p`
  color: #666;
  font-size: 13px;
  line-height: 1.6;
  margin: 0 0 16px 0;
`

const QrUrlText = styled.div`
  background: #f9f9f9;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  padding: 8px 12px;
  font-family: monospace;
  font-size: 12px;
  word-break: break-all;
  color: #333;
  margin: 16px auto 12px;
  max-width: 480px;
`

const LineSettings = () => {
  const [formData, setFormData] = useState({
    isEnabled: false,
    channelId: '',
    channelSecret: '',
    channelAccessToken: '',
    // 預約後請客人在 LINE 傳訊息給店家（LINE_CONTACT_PROMPT）
    contactPromptEnabled: false
  })
  const [basicId, setBasicId] = useState('')
  // OA 的回應模式：'chat' = 聊天已開；'bot' = 只開 Webhook（客人訊息不會進聊天室）
  const [chatMode, setChatMode] = useState(null)
  const [errors, setErrors] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const { user } = useAuth()
  const businessId = user.businessId

  // 生成 Webhook URL
  const webhookUrl = `https://api.beautefeel.com/api/line/webhook/${businessId}`

  // 現場綁定/配對 QR Code：客人掃描後進入前台 /line-connect。
  // 帶 businessId：若本店已啟用自有 LINE OA（本頁設定），客人走「店家 LINE 配對」
  // （產生配對碼→加店家 OA 好友→貼碼）；未啟用則走美感平台 LINE 綁定。
  // check-business-status 的回傳中，店名放在 name（businessName 為部分頁面的舊欄位，兜底用）
  const storeName = user.name || user.businessName || ''
  const qrParams = new URLSearchParams()
  if (businessId) qrParams.set('businessId', businessId)
  if (storeName) qrParams.set('storeName', storeName)
  const lineConnectUrl = `https://beautefeel.com/line-connect${
    qrParams.toString() ? `?${qrParams.toString()}` : ''
  }`

  const handleDownloadQr = () => {
    const canvas = document
      .getElementById('line-connect-qr')
      ?.querySelector('canvas')
    if (!canvas) {
      Toast.show({ content: 'QR Code 尚未產生完成', position: 'bottom' })
      return
    }
    const a = document.createElement('a')
    a.download = `LINE綁定QRCode-${storeName || 'store'}.png`
    a.href = canvas.toDataURL('image/png')
    a.click()
  }

  const handleCopyQrUrl = async () => {
    try {
      await navigator.clipboard.writeText(lineConnectUrl)
      Toast.show({ content: '已複製連結', position: 'bottom' })
    } catch {
      Toast.show({ content: '複製失敗，請手動複製', position: 'bottom' })
    }
  }

  const fetchLineSettings = useCallback(async () => {
    setIsLoading(true)
    try {
      const response = await api.get(
        `/api/business_B/${businessId}/line-config`
      )

      console.log('LINE API 回應:', response.data) // 調試用

      // 處理 API 回應資料結構
      const apiData = response.data.data || response.data

      setFormData((prevData) => ({
        ...prevData,
        isEnabled: apiData.isEnabled || false,
        channelId: apiData.channelId || '',
        channelSecret: apiData.channelSecret || '',
        channelAccessToken: apiData.channelAccessToken || '',
        contactPromptEnabled: apiData.contactPromptEnabled === true
      }))
      setBasicId(apiData.basicId || '')
      setChatMode(apiData.chatMode || null)
    } catch (error) {
      console.error('獲取 LINE 設定錯誤:', error)
      if (error.response?.status !== 404) {
        Toast.show({
          content: '獲取 LINE 設定失敗，請稍後再試',
          position: 'top'
        })
      }
    } finally {
      setIsLoading(false)
    }
  }, [businessId])

  useEffect(() => {
    fetchLineSettings()
  }, [fetchLineSettings])

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData((prevData) => ({
      ...prevData,
      [name]: type === 'checkbox' ? checked : value
    }))
    setErrors((prevErrors) => ({ ...prevErrors, [name]: '' }))
  }

  const validateForm = () => {
    const newErrors = {}
    if (formData.isEnabled) {
      if (!formData.channelId) newErrors.channelId = '請輸入 Channel ID'
      if (!formData.channelSecret)
        newErrors.channelSecret = '請輸入 Channel Secret'
      if (!formData.channelAccessToken)
        newErrors.channelAccessToken = '請輸入 Channel Access Token'
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (validateForm()) {
      setIsSaving(true)
      try {
        const { data } = await api.put(
          `/api/business_B/${businessId}/line-config`,
          formData
        )
        setBasicId(data?.basicId || '')
        // 重新讀一次，拿到最新的聊天模式
        if (formData.contactPromptEnabled) fetchLineSettings()
        Toast.show({
          content:
            formData.contactPromptEnabled && !data?.basicId
              ? 'LINE 設定已更新，但讀不到官方帳號 ID，請按「測試連接」確認 Token'
              : 'LINE 設定已更新',
          position: 'bottom'
        })
      } catch (error) {
        Toast.show({
          content: '更新失敗，請稍後再試',
          position: 'bottom'
        })
      } finally {
        setIsSaving(false)
      }
    }
  }

  const handleTestConnection = async () => {
    if (!formData.channelAccessToken) {
      Toast.show({
        content: '請先輸入 Channel Access Token',
        position: 'bottom'
      })
      return
    }

    setIsSaving(true)
    try {
      await api.post(`/api/business_B/${businessId}/line-config/test`, {
        channelAccessToken: formData.channelAccessToken
      })
      Toast.show({
        content: 'LINE 連接測試成功',
        position: 'bottom'
      })
    } catch (error) {
      Toast.show({
        content: 'LINE 連接測試失敗，請檢查設定',
        position: 'bottom'
      })
    } finally {
      setIsSaving(false)
    }
  }

  if (isLoading) {
    return (
      <Spin
        size='large'
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh'
        }}
      />
    )
  }

  return (
    <FormContainer onSubmit={handleSubmit}>
      <QrCard>
        <QrTitle>
          <QrcodeOutlined />
          現場 LINE 綁定 QR Code
        </QrTitle>
        <QrDesc>
          列印或展示這個 QR Code 在櫃檯，客人掃描後即可完成 LINE 綁定。
          若本店已啟用下方的 LINE Bot 整合（自有官方帳號），客人會走「與店家
          LINE 配對」流程（自動產生配對碼 → 加店家好友 → 貼上配對碼）；
          未啟用則走美感平台 LINE 綁定。之後預約確認與提醒通知都會透過 LINE
          傳送給客人。
        </QrDesc>
        <div
          id='line-connect-qr'
          style={{ display: 'flex', justifyContent: 'center' }}
        >
          <QRCode value={lineConnectUrl} size={200} />
        </div>
        <QrUrlText>{lineConnectUrl}</QrUrlText>
        <ButtonGroup>
          <Button icon={<DownloadOutlined />} onClick={handleDownloadQr}>
            下載 QR Code
          </Button>
          <Button icon={<CopyOutlined />} onClick={handleCopyQrUrl}>
            複製連結
          </Button>
        </ButtonGroup>
      </QrCard>

      <InfoCard>
        <InfoTitle>
          <InfoCircleOutlined />
          LINE Bot 整合說明
        </InfoTitle>
        <InfoText>
          整合 LINE Bot 後，您可以透過 LINE 接收預約通知、與客戶互動。 請先前往
          LINE Developers Console 建立 Messaging API
          Channel，並取得相關設定資訊。
        </InfoText>
        <div style={{ marginTop: '12px' }}>
          <LinkButton
            href='https://developers.line.biz/console/'
            target='_blank'
            rel='noopener noreferrer'
          >
            前往 LINE Developers Console <LinkOutlined />
          </LinkButton>
        </div>
      </InfoCard>

      <SwitchContainer>
        <SwitchText>
          啟用 LINE Bot 整合
          <StatusText enabled={formData.isEnabled}>
            {formData.isEnabled ? '啟用中' : '已停用'}
          </StatusText>
        </SwitchText>
        <SwitchLabel>
          <SwitchInput
            type='checkbox'
            name='isEnabled'
            checked={formData.isEnabled}
            onChange={handleChange}
          />
          <Slider />
        </SwitchLabel>
      </SwitchContainer>

      <FormGroup>
        <Label htmlFor='channelId'>Channel ID</Label>
        <Input
          type='text'
          id='channelId'
          name='channelId'
          placeholder='請輸入 LINE Channel ID'
          value={formData.channelId}
          onChange={handleChange}
          disabled={!formData.isEnabled}
        />
        {errors.channelId && <ErrorMessage>{errors.channelId}</ErrorMessage>}
        <HintText>
          可在 LINE Developers Console 的 Basic settings 頁面找到
        </HintText>
      </FormGroup>

      <FormGroup>
        <Label htmlFor='channelSecret'>Channel Secret</Label>
        <Input
          type='text'
          id='channelSecret'
          name='channelSecret'
          placeholder='請輸入 LINE Channel Secret'
          value={formData.channelSecret}
          onChange={handleChange}
          disabled={!formData.isEnabled}
        />
        {errors.channelSecret && (
          <ErrorMessage>{errors.channelSecret}</ErrorMessage>
        )}
        <HintText>
          可在 LINE Developers Console 的 Basic settings 頁面找到
        </HintText>
      </FormGroup>

      <FormGroup>
        <Label htmlFor='channelAccessToken'>Channel Access Token</Label>
        <Input
          type='text'
          id='channelAccessToken'
          name='channelAccessToken'
          placeholder='請輸入 LINE Channel Access Token'
          value={formData.channelAccessToken}
          onChange={handleChange}
          disabled={!formData.isEnabled}
        />
        {errors.channelAccessToken && (
          <ErrorMessage>{errors.channelAccessToken}</ErrorMessage>
        )}
        <HintText>
          可在 LINE Developers Console 的 Messaging API 頁面產生長期的 Channel
          Access Token
        </HintText>
      </FormGroup>

      <FormGroup>
        <Label>Webhook URL（請複製此網址到 LINE Console）</Label>
        <WebhookContainer>{webhookUrl}</WebhookContainer>
        <HintText>
          請將此 URL 設定到 LINE Developers Console 的 Messaging API 設定中的
          Webhook URL， 並確保啟用「Use webhook」選項
        </HintText>
      </FormGroup>

      {/* START: LINE_CONTACT_PROMPT */}
      <SwitchContainer>
        <SwitchText>
          預約後請客人傳訊息給店家
          <StatusText enabled={formData.contactPromptEnabled}>
            {formData.contactPromptEnabled ? '啟用中' : '已停用'}
          </StatusText>
        </SwitchText>
        <SwitchLabel>
          <SwitchInput
            type='checkbox'
            name='contactPromptEnabled'
            checked={formData.contactPromptEnabled}
            onChange={handleChange}
            disabled={!formData.isEnabled}
          />
          <Slider />
        </SwitchLabel>
      </SwitchContainer>
      <HintText>
        開啟後，客人預約成功收到的 LINE 通知與預約完成頁，會多一顆「傳送預約資訊給店家」按鈕。
        客人按下後，會以他自己的 LINE 傳一則含預約編號的訊息到本店官方帳號，
        您就能在 LINE 官方帳號後台的聊天室看到這位客人，直接跟他確認項目與時間。
        客人傳過訊息的預約，在預約詳情會顯示「客人已在 LINE 聯繫」。
        {basicId ? `（本店官方帳號：${basicId}）` : ''}
      </HintText>
      {formData.contactPromptEnabled && chatMode === 'chat' && (
        <Hint tone='success' title='官方帳號「聊天」已開啟'>
          客人傳來的預約訊息會出現在 LINE 官方帳號後台的聊天室，請記得回覆。
        </Hint>
      )}
      {formData.contactPromptEnabled && chatMode !== 'chat' && (
        <Hint
          tone='warning'
          title={
            chatMode === 'bot'
              ? '本店官方帳號目前沒有開啟「聊天」'
              : '請確認 LINE 官方帳號後台已開啟「聊天」'
          }
        >
          LINE Official Account Manager →「設定」→「回應設定」：「聊天」要開啟
          （可以和 Webhook 同時開）。只開 Webhook 的話，客人傳來的訊息不會出現在聊天室。
          建議同時關閉「自動回應訊息」，避免客人收到重複的罐頭回覆。
        </Hint>
      )}
      {/* END: LINE_CONTACT_PROMPT */}

      <ButtonGroup>
        <StyledButton
          type='default'
          onClick={handleTestConnection}
          disabled={
            !formData.isEnabled || isSaving || !formData.channelAccessToken
          }
          loading={isSaving}
        >
          測試連接
        </StyledButton>
        <StyledButton
          type='primary'
          htmlType='submit'
          disabled={isLoading}
          loading={isSaving}
        >
          {isSaving ? '保存中...' : '保存設定'}
        </StyledButton>
      </ButtonGroup>
    </FormContainer>
  )
}

export default LineSettings
